import React, { useState } from 'react';
import {
    marketplaceTypes,
    productTypes,
} from 'mayhem-taxonomy';
import Form from '../../Form';
import Alert from '../../Alert';
import TranslatableField from '../../TranslatableField';
import {
    getMarketCurrencySymbol,
    normalizeMarketCurrency,
} from '../../../helpers/marketSettings';
import useDebouncedAutosave from './useDebouncedAutosave';

const SELLER_PRODUCT_TYPES = new Set([...marketplaceTypes, 'subscription']);
const PRODUCT_TYPES = productTypes.filter(({value}) => SELLER_PRODUCT_TYPES.has(value));

const MESSAGE_TYPES = ['physical', 'custom', 'commission', 'service', 'rental'];

export default function ProductWizardBasics({
    product,
    onNext,
    showListingControls = false,
    saving,
    error,
    defaultLanguage,
    defaultCurrency,
    onTranslationsChange,
    onAutosave,
}) {
    const supportsPayWhatYouWant = values => !['subscription', 'upgrade'].includes(values.type);
    const currency = normalizeMarketCurrency(
        product?.currency,
        normalizeMarketCurrency(defaultCurrency),
    );
    const [values, setValues] = useState({
        type: product?.type || 'digital',
        name: product?.name || '',
        description: product?.description || '',
        price: product ? (product.price / 100).toFixed(2) : '',
        period: product?.period || 'month',
        allowedUserMessages: product?.allowedUserMessages ?? 0,
        isAvailable: product?.isAvailable ?? false,
    });

    const buildPayload = () => {
        const payload = {
            type: values.type,
            name: values.name,
            description: values.description,
            price: parseFloat(values.price) || 0,
            currency,
        };
        if (values.type === 'subscription') {
            payload.period = values.period;
        }
        if (MESSAGE_TYPES.includes(values.type)) {
            payload.allowedUserMessages = parseInt(values.allowedUserMessages, 10);
        }
        if (showListingControls) {
            payload.isAvailable = values.isAvailable;
        }
        return payload;
    };
    const autosavePayload = buildPayload();
    const {cancelAutosave} = useDebouncedAutosave(
        autosavePayload,
        onAutosave,
        {enabled: Boolean(product?.uuid)},
    );

    const translationsEndpoint = product?.uuid
        ? `/v1/seller/products/${product.uuid}/translations`
        : null;

    const fields = [
        {
            name: 'type',
            label: 'Product Type',
            type: 'select',
            required: true,
            options: PRODUCT_TYPES,
        },
        {
            name: 'name',
            label: 'Product Name',
            type: 'text',
            required: true,
            placeholder: 'Enter your product name (at least 5 characters)',
            maxLength: 60,
            labelAddon: translationsEndpoint ? (
                <TranslatableField
                    endpoint={translationsEndpoint}
                    field="name"
                    label="Product Name"
                    sourceValue={values.name}
                    defaultLanguage={defaultLanguage}
                    maxLength={60}
                    translations={product?.translations}
                    onTranslationsChange={onTranslationsChange}
                />
            ) : null,
        },
        {
            name: 'description',
            label: 'Short Description',
            type: 'textarea',
            rows: 2,
            placeholder: 'One-line description shown in listings',
            help: 'Keep this brief — it appears in search results and product cards.',
            maxLength: 180,
            labelAddon: translationsEndpoint ? (
                <TranslatableField
                    endpoint={translationsEndpoint}
                    field="description"
                    label="Short Description"
                    sourceValue={values.description}
                    defaultLanguage={defaultLanguage}
                    inputType="textarea"
                    maxLength={180}
                    translations={product?.translations}
                    onTranslationsChange={onTranslationsChange}
                />
            ) : null,
        },
        {
            name: 'price',
            label: `Price (${currency} — ${getMarketCurrencySymbol(currency)})`,
            type: 'number',
            step: 0.01,
            min: 0,
            placeholder: '0.00',
            required: true,
            help: supportsPayWhatYouWant(values)
                ? 'You can turn this into a Pay What You Want recommended price on the Details step.'
                : undefined,
        },
        ...(values.type === 'subscription' ? [{
            name: 'period',
            label: 'Subscription Period',
            type: 'select',
            options: [
                {value: 'day', label: 'Daily'},
                {value: 'week', label: 'Weekly'},
                {value: 'month', label: 'Monthly'},
                {value: 'year', label: 'Yearly'},
            ],
        }] : []),
        ...(MESSAGE_TYPES.includes(values.type) ? [{
            name: 'allowedUserMessages',
            label: 'Allowed Customer Messages',
            type: 'select',
            options: [
                {value: 0, label: 'None'},
                {value: 1, label: '1 message'},
                {value: 2, label: '2 messages'},
                {value: 3, label: '3 messages'},
                {value: 5, label: '5 messages'},
            ],
            help: 'How many messages can a customer send after purchasing?',
        }] : []),
        ...(showListingControls ? [{
            name: 'isAvailable',
            label: 'For Sale',
            type: 'checkbox',
            placeholder: 'Make this product available for sale on your storefront',
            help: 'Turn this off to keep the product as a draft.',
        }] : []),
    ];

    const submitBasics = () => {
        cancelAutosave();
        onNext(buildPayload());
    };

    return (
        <div className="wizard-step">
            <Form fields={fields} values={values} onChange={setValues} />
            {error && <Alert type="error">{error}</Alert>}
            <div className="wizard-nav">
                <button type="button" className="button primary" onClick={submitBasics} disabled={saving}>
                    {saving ? 'Saving…' : 'Next: Details →'}
                </button>
            </div>
        </div>
    );
}
